import { useInfiniteQuery, type UseInfiniteQueryOptions } from '@tanstack/react-query';

import type { PaginatedRequestParams, PaginatedResponse } from '@/types/api';

type PageParam = {
    pageNumber: number;
    skipCount: number;
};

type UseAmelioInfiniteQueryOptions<T> = {
    queryKey: unknown[];
    queryFn: (params: PaginatedRequestParams) => Promise<PaginatedResponse<T>>;
    pageSize: number;
    getPageSizeForPage?: (pageNumber: number) => number;
    options?: Omit<
        UseInfiniteQueryOptions<PaginatedResponse<T>, Error>,
        'queryKey' | 'queryFn' | 'getNextPageParam' | 'initialPageParam' | 'select'
    >;
    meta?: Record<string, unknown>;
};

export const useAmelioInfiniteQuery = <T>({
    queryKey,
    queryFn,
    pageSize,
    getPageSizeForPage,
    options,
    meta,
}: UseAmelioInfiniteQueryOptions<T>) => {
    return useInfiniteQuery<PaginatedResponse<T>, Error>({
        queryKey: [...queryKey, pageSize],
        queryFn: ({ pageParam }) => {
            const { pageNumber, skipCount } = pageParam as PageParam;
            const currentPageSize = getPageSizeForPage ? getPageSizeForPage(pageNumber) : pageSize;
            return queryFn({ pageNumber, pageSize: currentPageSize, skipCount, meta });
        },
        getNextPageParam: (lastPage, allPages) => {
            if (!lastPage.hasNextPage) {
                return undefined;
            }

            // Calculate total items returned so far
            const totalItemsReturned = allPages.reduce((sum, page) => sum + page.itemCount, 0);
            const nextPageNumber = allPages.length + 1;

            return {
                pageNumber: nextPageNumber,
                skipCount: totalItemsReturned,
            };
        },
        initialPageParam: {
            pageNumber: 1,
            skipCount: 0,
        },
        ...options,
    });
};
